import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  WithCUToggleCommandsAndData,
  WithRecordID,
  WithData,
} from "../../../models/shared/CommonModels";
import {
  Email,
  EmailPostModel,
  EmailPutModel,
  EmailRequest,
} from "../../../models/EmailModel";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { REQUIRED_HELPER_TEXT } from "../../../utils/form";
import { createEmail, editEmail, fetchEmail } from "../../../api/emails";
import {
  fetchEmailsQueryKey,
  fetchSingleEmailQueryKey,
} from "../../../utils/QueryClient";
import { AxiosError } from "axios";
import { fromAxiosErrorToMessage } from "../../../utils/crud";
import ErrorMessageAlertComponent from "../../errors/ErrorMessageAlertComponent";

const CustomerEmailForm: React.FC<WithCUToggleCommandsAndData<Email>> = ({
  initialData,
  open,
  toggleOpen,
  editEnabled,
  onClose,
}) => {
  const queryClient = useQueryClient();

  const [hideError, setHideError] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [email, setEmail] = useState<string>();
  const [isPec, setIsPec] = useState<boolean>(false);
  const [preferred, setPreferred] = useState<boolean>();

  const query = useQuery({
    queryKey: [fetchSingleEmailQueryKey, initialData.id],
    queryFn: () => fetchEmail(initialData.id),
    enabled: !!initialData.id,
    onSuccess: (getResponse) => {
      setEmail(getResponse.data.email);
      setIsPec(getResponse.data.pec === 1 ? true : false);
      setPreferred(getResponse.data.preferred);
    },
    refetchOnWindowFocus: false,
    onError: (err: AxiosError) => {
      setHideError(false);
      setErrorMessage(fromAxiosErrorToMessage(err));
    },
  });

  const createMutation = useMutation({
    mutationFn: (x: WithData) => createEmail(x),
    onSuccess: () => {
      queryClient.invalidateQueries(fetchEmailsQueryKey);
      resetFields();
      toggleOpen();
    },
    onError: (err: AxiosError) => {
      setHideError(false);
      setErrorMessage(fromAxiosErrorToMessage(err));
    },
  });

  const editMutation = useMutation({
    mutationFn: (x: WithRecordID) => editEmail(x),
    onSuccess: () => {
      queryClient.invalidateQueries(fetchEmailsQueryKey);
      resetFields();
      toggleOpen();
    },
    onError: (err: AxiosError) => {
      setHideError(false);
      setErrorMessage(fromAxiosErrorToMessage(err));
    },
  });

  const resetFields = () => {
    // resetting fields
    setEmail("");
    setIsPec(false);
    setPreferred(false);
  };

  if (query.isFetching || query.isLoading) return <Skeleton animation="wave" />;

  const closeActions = () => {
    toggleOpen();
    if (!hideError) {
      queryClient.invalidateQueries(fetchEmailsQueryKey);
    }
  };

  const handleSubmit = () => {
    if (email === undefined || isPec === undefined) {
      return;
    }

    if (editEnabled) {
      const emailTemplate = {} as EmailPutModel;
      emailTemplate.email = email;
      emailTemplate.pec = isPec ? 1 : 0;
      emailTemplate.preferred = preferred!;

      const emailData = {} as EmailRequest;
      const postData = {} as WithRecordID;
      emailData.emailcliente = emailTemplate;
      postData.data = emailData;
      postData.recordID = initialData.id;

      editMutation.mutate(postData);
    } else {
      const emailPost = {} as EmailPostModel;
      emailPost.email = email;
      emailPost.pec = isPec ? 1 : 0;
      emailPost.id_cliente = initialData.id_cliente;
      emailPost.preferred = preferred!;

      const postData = {} as WithData;
      const body = {} as EmailRequest;
      body.emailcliente = emailPost;
      postData.data = body;

      createMutation.mutate(postData);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={closeActions}
        PaperProps={{
          component: "form",
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
          },
        }}
      >
        <DialogTitle>
          {editEnabled ? "Modifica" : "Crea Nuova"} Email Cliente
        </DialogTitle>
        <DialogContent style={{ width: 500 }}>
          <ErrorMessageAlertComponent
            error_message={errorMessage}
            hide={() => {
              setHideError(true);
            }}
            visible={!hideError}
          />

          <Typography sx={{ fontSize: "12px", color: "grey" }}>
            {REQUIRED_HELPER_TEXT}
          </Typography>

          <TextField
            margin="normal"
            required
            fullWidth
            label="Email"
            name="email"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Box display="flex" justifyContent="left">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPec ? true : false}
                  onClick={() => setIsPec(!isPec)}
                />
              }
              label="PEC"
            />
          </Box>

          <Box display="flex" justifyContent="left">
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferred ? true : false}
                  onClick={() => setPreferred(!preferred)}
                />
              }
              label="Preferito"
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            paddingLeft: "18pt",
            paddingRight: "18pt",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ justifyContent: "flex-start" }}>
            <Button
              onClick={() => {
                if (onClose !== undefined) {
                  onClose();
                }

                if (!editEnabled) {
                  resetFields();
                }

                closeActions();
              }}
            >
              Chiudi
            </Button>
          </Box>
          <Box sx={{ justifyContent: "flex-end" }}>
            <Button onClick={() => handleSubmit()} variant="contained">
              {editEnabled ? "Salva" : "Aggiungi"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerEmailForm;
